<template>
    <div class="js-transfer-record-table" @click="handleOutsideClick">
        <!-- 搜索表单 -->
        <div class="search-form">
            <!-- 搜索表单标题和展开/收缩按钮 -->
            <div class="search-form-header">
                <h3 class="search-form-title">搜索和筛选</h3>
                <a-button
                    type="link"
                    @click="toggleSearchFormExpanded"
                    class="expand-toggle-btn"
                    :title="searchFormExpanded ? '收缩搜索面板' : '展开搜索面板'"
                >
                    <a-icon :type="searchFormExpanded ? 'up' : 'down'" />
                    {{ searchFormExpanded ? '收缩' : '展开' }}
                </a-button>
            </div>

            <!-- 可展开/收缩的搜索表单内容 -->
            <a-collapse-transition>
                <div v-show="searchFormExpanded" class="search-form-content">
                    <a-row :gutter="16">
                        <a-col :span="24">
                            <a-form layout="inline" style="justify-content: flex-start">
                                <a-form-item label="时间范围">
                                    <a-range-picker v-model="searchForm.dateRange" format="YYYY-MM-DD"
                                        @change="handleDateRangeChange" :ranges="dateRanges" style="width: 200px" />
                                </a-form-item>
                                <a-form-item label="搜索字段">
                                    <a-select v-model="searchForm.searchField" style="width: 150px" placeholder="请选择字段">
                                        <a-select-option v-for="col in searchableColumns" :key="col.key" :value="col.key">
                                            {{ col.title }}
                                        </a-select-option>
                                    </a-select>
                                </a-form-item>
                                <a-form-item label="搜索方式">
                                    <a-select v-model="searchForm.searchType" style="width: 80px">
                                        <a-select-option value="contains">包含</a-select-option>
                                        <a-select-option value="equals">等于</a-select-option>
                                    </a-select>
                                </a-form-item>
                                <a-form-item label="搜索内容">
                                    <a-input v-model="searchForm.searchValue" placeholder="请输入搜索内容" style="width: 200px" />
                                </a-form-item>
                                <a-form-item label="收支属性">
                                    <a-select v-model="searchForm.incomeExpenseAttribute" style="width: 150px"
                                        placeholder="请选择收支属性">
                                        <a-select-option value="">全部</a-select-option>
                                        <a-select-option value="收入">收入</a-select-option>
                                        <a-select-option value="支出">支出</a-select-option>
                                    </a-select>
                                </a-form-item>
                                <a-form-item>
                                    <a-button type="primary" @click="handleSearch">
                                        <a-icon type="search" />搜索
                                    </a-button>
                                    <a-button style="margin-left: 8px" @click="handleReset">
                                        <a-icon type="reload" />重置
                                    </a-button>
                                </a-form-item>
                                <br />
                                <a-form-item label="替换字段">
                                    <a-select v-model="searchForm.replaceField" style="width: 150px" placeholder="请选择字段">
                                        <a-select-option v-for="col in searchableColumns" :key="col.key" :value="col.key">
                                            {{ col.title }}
                                        </a-select-option>
                                    </a-select>
                                </a-form-item>
                                <a-form-item>
                                    <a-checkbox v-model="searchForm.replaceWholeField">替换整个字段</a-checkbox>
                                </a-form-item>

                                <a-form-item label="替换搜索">
                                    <a-input v-model="searchForm.replaceSearchValue" placeholder="请输入要替换的内容"
                                        style="width: 150px" />
                                </a-form-item>
                                <a-form-item label="替换内容">
                                    <a-input v-model="searchForm.replaceValue" placeholder="请输入替换后的内容" style="width: 200px" />
                                </a-form-item>
                                <a-form-item>
                                    <a-button type="primary" @click="handleReplace" :disabled="!canReplace">
                                        <a-icon type="swap" />替换
                                    </a-button>
                                    <a-button style="margin-left: 8px" @click="handleReset">
                                        <a-icon type="reload" />重置
                                    </a-button>
                                </a-form-item>
                            </a-form>
                        </a-col>
                    </a-row>
                </div>
            </a-collapse-transition>
            <!-- 优化布局：将统计信息和负余额警告放在同一行 -->
            <a-row :gutter="16" class="info-row">
                <a-col :xs="24" :sm="24" :md="16" :lg="18" :xl="18">
                    <div class="statistics-info compact">
                        <div class="statistics-item">
                            <span class="label">总收入:</span>
                            <span class="income">{{ formatAmount(totalIncome) }}</span>
                        </div>
                        <div class="statistics-item">
                            <span class="label">总支出:</span>
                            <span class="expense">{{ formatAmount(totalExpense) }}</span>
                        </div>
                        <template v-if="hasSelected">
                            <div class="statistics-item">
                                <span class="label">选中收入:</span>
                                <span class="income">{{ formatAmount(selectedTotalIncome) }}</span>
                            </div>
                            <div class="statistics-item">
                                <span class="label">选中支出:</span>
                                <span class="expense">{{ formatAmount(selectedTotalExpense) }}</span>
                            </div>
                        </template>
                    </div>
                </a-col>
                <a-col :xs="24" :sm="24" :md="8" :lg="6" :xl="6">
                    <!-- 优化的负数余额提示区域 -->
                    <div v-if="negativeBalanceRecord" class="negative-balance-warning compact">
                        <div class="negative-balance-content">
                            <span class="warning-label">发现余额为负:</span>
                            <a-tag color="orange" @click="scrollToRow(negativeBalanceRecord)"
                                size="small" class="negative-balance-tag">
                                <span class="tag-content">
                                    {{ formatNegativeBalanceText(negativeBalanceRecord) }}
                                </span>
                            </a-tag>
                        </div>
                    </div>
                </a-col>
            </a-row>
        </div>

        <!-- 工具栏 -->
        <div class="table-toolbar">
            <div class="left">
                <a-button-group size="small">
                    
                    <a-button type="primary" @click="handleAdd">
                        <a-icon type="plus" />添加
                    </a-button>
                    <a-button type="primary" @click="handleBatchDelete" :disabled="!hasSelected">
                        <a-icon type="delete" />批量删除
                    </a-button>
                    <a-button type="primary" @click="handleExport">
                        <a-icon type="download" />导出Excel
                    </a-button>
                    <a-button type="primary" @click="showImportExcelModal = true">
                        <a-icon type="upload" />导入Excel
                    </a-button>
                    <!-- 批量复制 -->
                    <a-button type="primary" @click="handleBatchCopy">
                        <a-icon type="copy" />批量复制
                    </a-button>
                </a-button-group>
            </div>
            <div class="right">
                <span v-if="hasSelected" style="margin-right: 8px">
                    已选择 {{ selectedRowKeys.length }} 项
                </span>
            </div>
        </div>
        <!-- 表格容器 -->
        <div class="table-container">
            <!-- 根据平台动态显示不同的表格组件 -->
            <!-- <component :is="currentTableComponent" 
                :recordData="recordData"
                @selection-change="handleSelectionChange"
                @copy="handleCopy"
                @delete="handleDelete"
                @data-change="handleDataChange"
                ref="currentTable">
            </component> -->
            <vxe-grid v-bind="recordTableOptions" @edit-closed="handleDataChange" ref="recordTable"
                @checkbox-change="handleCheckboxChange" @checkbox-all="handleCheckboxAll">
                <template #operation="{ row }">
                    <a-button type="link" @click="handleCopy([row.id])">复制</a-button>
                    <a-button type="link" style="color: #ff4d4f" @click="handleDelete([row.id])">删除</a-button>
                </template>
            </vxe-grid>
        </div>

        <!-- 表格底部信息 -->
        <div class="table-footer">
            <span class="total-info">共 {{ pagination.total }} 条记录</span>
        </div>

        <!-- 导入Excel模态框 -->
        <a-modal title="导入Excel" :visible="showImportExcelModal" @cancel="showImportExcelModal = false"
            @ok="handleImportExcel" :confirmLoading="importLoading">
            <p>请选择要导入的Excel文件，支持.xlsx和.xls格式</p>
            <a-upload :file-list="excelFileList" :before-upload="beforeUploadExcel"
                :remove="() => { excelFileList = [] }">
                <a-button>
                    <a-icon type="upload" /> 选择文件
                </a-button>
            </a-upload>
            <div style="margin-top: 16px;">
                <a-checkbox v-model="importOptions.overwriteData">覆盖全部数据</a-checkbox>
            </div>
            <div style="margin-top: 16px; color: #999;">
                <p>注意：</p>
                <p>1. 导入的Excel文件必须包含与表格列名相同的列</p>
                <p>2. 导入会根据Excel中的数据添加新记录</p>
                <p>3. 选择"覆盖全部数据"时，将先清空该用户所有历史记录，再导入新数据</p>
                <p>4. 文件大小不能超过100MB</p>
            </div>
        </a-modal>
    </div>
</template>

<script>
import moment from 'moment'
import * as XLSX from 'xlsx'
import request from '@/utils/request'
import { API } from '@/config/api'
// 引入JsColumns.json

import { RecycleScroller } from 'vue-virtual-scroller'
import 'vue-virtual-scroller/dist/vue-virtual-scroller.css'

import XEUtils from 'xe-utils'

export default {
    name: 'TransferRecordTable',
    components: {
        RecycleScroller,
    },
    props: {
        userId: {
            type: [String, Number],
            required: true
        },
        platform: {
            type: String,
            required: true
        }
    },
    data() {
        return {
            loading: false,
            recordData: [],
            selectedRowKeys: [],
            selectedRows: [],
            selectedRowsVersion: 0,
            searchFormExpanded: true, // 搜索表单展开状态
            searchForm: {
                dateRange: [],
                searchField: '',
                searchValue: '',
                searchType: 'contains',
                replaceField: '',
                replaceSearchValue: '',
                replaceValue: '',
                incomeExpenseAttribute: '',
                replaceWholeField: false
            },
            searchableColumns: [],
            editColumns: [],
            columns: [],
            pagination: {
                total: 0  // 仅保留总数信息用于显示
            },
            modalVisible: false,
            modalTitle: '添加记录',
            currentRecord: {
                transaction_date: moment(),
                posting_date: moment(),
                transaction_amount: 0,
                balance: 0,
                merchant_code: '',
                summary: '消费',
                counterparty_account: '',
                counterparty_name: '',
                income_expense_attribute: '支出'
            },
            currentRecordIndex: -1,
            rules: {

            },
            cacheData: [],
            totalIncome: 0,
            totalExpense: 0,
            showImportExcelModal: false,
            excelFileList: [],
            importLoading: false,
            recordTableOptions: {
                id: 'recordTable',
                customConfig: {
                    storage: true
                },
                rowConfig: {
                    isCurrent: true,
                    isHover: true
                },
                editConfig: {
                    trigger: 'click',
                    mode: 'cell'
                },
                checkboxConfig: {
                    trigger: 'row',
                    highlight: true,
                    range: false,
                    strict: true
                },
                size: 'mini',
                columns: [],
                data: [],
                border: true,
                showOverflow: true,
                height: 600,
                resizableConfig: {
                    isDblclickAutoWidth: true
                },
                columnConfig: {
                    resizable: true,
                    drag: true
                },
                virtualYConfig: {
                    enabled: true,
                    gt: 0
                },
                virtualXConfig: {
                    enabled: true,
                    gt: 0
                },
                showHeaderOverflow: true,
                menuConfig: {
                    body: {
                        options: [
                            [
                                { code: 'copy', name: '复制' },
                                { code: 'delete', name: '删除' },
                            ]
                        ]
                    }
                },
                cellStyle: ({ row, column, rowIndex, columnIndex }) => {
                    if (column.field == 'transaction_amount') {
                        if (Number(row.transaction_amount) > 0) {
                            return { color: 'red' }
                        } else if (Number(row.transaction_amount) < 0) {
                            return { color: 'green' }
                        }
                    }
                    if (column.field == 'balance') {
                        if (Number(row.balance) > 0) {
                            return { color: 'red' }
                        } else if (Number(row.balance) < 0) {
                            return { color: 'green' }
                        }
                    }
                }
            },
            isShift: false,
            selectedIndexes: [0, 0],
            columnWidths: {
                js: {},
                zg: {},
                yz: {},
                ny: {},
                wx: {},
                gs: {},
                pa: {}
            },
            negativeBalanceRecord: null,
            importOptions: {
                overwriteData: false
            }
        }
    },
    created() {
        this.initColumns();
        // 恢复搜索表单展开状态
        this.restoreSearchFormExpandedState();
        this.restoreSearchForm();
        this.fetchRecordData();
    },
    mounted() {
        // 在组件挂载后绑定事件监听器
        this.bindKeyboardEvents();
        // 绑定窗口焦点事件，用于重置状态
        window.addEventListener('blur', this.handleWindowBlur);
        window.addEventListener('focus', this.handleWindowFocus);
        // 监听页面可见性变化
        document.addEventListener('visibilitychange', this.handleVisibilityChange);
    },
    activated() {
        // keep-alive 组件激活时重新绑定事件
        this.bindKeyboardEvents();
        this.resetShiftState();
    },
    deactivated() {
        // keep-alive 组件失活时解绑事件
        this.unbindKeyboardEvents();
        this.resetShiftState();
    },
    beforeDestroy() {
        // 移除所有事件监听器，防止内存泄漏
        this.unbindKeyboardEvents();
        window.removeEventListener('blur', this.handleWindowBlur);
        window.removeEventListener('focus', this.handleWindowFocus);
        document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    },
    computed: {
        dateRanges() {
            return {
                '今天': [moment().startOf('day'), moment().endOf('day')],
                '本周': [moment().startOf('week'), moment().endOf('week')],
                '本月': [moment().startOf('month'), moment().endOf('month')],
                '最近三个月': [moment().subtract(3, 'month'), moment()],
                '最近半年': [moment().subtract(6, 'month'), moment()],
                '最近一年': [moment().subtract(1, 'year'), moment()]
            }
        },
        rowSelection() {
            return {
                selectedRowKeys: this.selectedRowKeys,
                onChange: (selectedRowKeys, selectedRows) => {
                    this.onSelectionChange(selectedRowKeys, selectedRows)
                },
                fixed: true,
                columnWidth: 60,
                onSelect: (record, selected, selectedRows, nativeEvent) => {
                    if (nativeEvent?.target?.closest('.editable-cell')) {
                        return
                    }
                    if (nativeEvent?.target?.closest('.editable-row-operations')) {
                        return
                    }
                    const newSelectedKeys = selected
                        ? [...this.selectedRowKeys, record.key]
                        : this.selectedRowKeys.filter(key => key !== record.key)
                    this.onSelectionChange(newSelectedKeys, selectedRows)
                }
            }
        },
        hasSelected() {
            return this.selectedRowKeys.length > 0
        },
        canReplace() {
            return this.searchForm.replaceField && this.searchForm.replaceSearchValue && this.searchForm.replaceValue
        },
        currentTableComponent() {
            const platformMap = {
                'js': 'JsTable',
                'zg': 'ZgTable',
                'yz': 'YzTable',
                'ny': 'NyTable',
                'wx': 'WxTable',
                'gs': 'GsTable'
            };
            return platformMap[this.platform] || 'JsTable';
        },
        selectedTotalIncome() {
            const version = this.selectedRowsVersion;
            console.log('selectedRows', this.selectedRows, 'version', version)
            if (!this.selectedRows || this.selectedRows.length === 0) return 0;
            return this.selectedRows.reduce((sum, row) => {
                const amount = Number(row.transaction_amount);
                return sum + (amount > 0 ? amount : 0);
            }, 0);
        },
        selectedTotalExpense() {
            const version = this.selectedRowsVersion;
            if (!this.selectedRows || this.selectedRows.length === 0) return 0;
            return this.selectedRows.reduce((sum, row) => {
                const amount = Number(row.transaction_amount);
                return sum + (amount < 0 ? amount : 0);
            }, 0);
        }
    },
    watch: {
        // 监听搜索字段的变化
        'searchForm.searchField'(val) {
            this.saveSearchForm();
        },
        'searchForm.searchValue'(val) {
            this.saveSearchForm();
        },
        'searchForm.searchType'(val) {
            this.saveSearchForm();
        },
        'searchForm.incomeExpenseAttribute'(val) {
            this.saveSearchForm();
        },
        // 监听替换字段的变化
        'searchForm.replaceField'(val) {
            this.saveSearchForm();
        },
        'searchForm.replaceSearchValue'(val) {
            this.saveSearchForm();
        },
        'searchForm.replaceValue'(val) {
            this.saveSearchForm();
        },
        'searchForm.replaceWholeField'(val) {
            this.saveSearchForm();
        }
    },
    methods: {
        // 切换搜索表单展开/收缩状态
        toggleSearchFormExpanded() {
            this.searchFormExpanded = !this.searchFormExpanded;
            // 保存状态到本地存储
            const storageKey = `search_form_expanded_${this.platform}_${this.userId}`;
            localStorage.setItem(storageKey, JSON.stringify(this.searchFormExpanded));
        },
        // 恢复搜索表单展开状态
        restoreSearchFormExpandedState() {
            try {
                const storageKey = `search_form_expanded_${this.platform}_${this.userId}`;
                const savedState = localStorage.getItem(storageKey);
                if (savedState !== null) {
                    this.searchFormExpanded = JSON.parse(savedState);
                }
            } catch (error) {
                console.warn('恢复搜索表单状态失败:', error);
                this.searchFormExpanded = true; // 默认展开
            }
        },
        initColumns() {
            let columns =  require('@/config/bankTransferRecordColumns.json')
            this.columns = columns[this.platform]
            console.log('columns', this.columns)
            // 根据当前平台的列配置初始化可搜索列
            const currentColumns = this.columns
            this.searchableColumns = currentColumns.filter(col => col.search).map(col => ({
                key: col.dataIndex,
                title: col.title
            }))

            let recordTableColumns = [];

            for (let i = 0; i < this.columns.length; i++) {
                const column = this.columns[i];
                let formatter = ''
                // let className = ''
                if (column.dataIndex === 'transaction_amount' || column.dataIndex === 'balance') {
                    formatter = ({ cellValue }) => {
                        return XEUtils.commafy(Number(cellValue), { digits: 2 })
                    }
                }
                recordTableColumns.push({
                    field: column.dataIndex,
                    title: column.title,
                    formatter: formatter,
                    filters: [{ data: '' }],
                    type: column.type || 'text',
                    editRender: column.editRender || { name: 'input' },
                    slots: column.slots || {},
                    // className: className
                })
            }

            this.recordTableOptions.columns = recordTableColumns

            // 恢复保存的搜索条件
            this.restoreSearchForm()
        },
        // 清空所有选择
        clearSelection() {
            // 获取表格组件实例
            const tableComponent = this.$refs.currentTable;
            if (tableComponent) {
                tableComponent.clearSelection();
                this.selectedRowKeys = [];
                this.selectedRecords = [];
            }
        },
        formatTime(timestamp) {
            return moment(timestamp * 1000).format('YYYY-MM-DD HH:mm:ss')
        },
        handleDateRangeChange(dates) {
            this.searchForm.dateRange = dates;
            // 无论日期范围是否为空，都保存搜索表单并执行搜索
            this.saveSearchForm();
            this.handleSearch();
        },
        handleSearch() {
            // 清空所有选择状态
            this.selectedRowKeys = [];
            this.selectedRows = [];
            this.selectedRowsVersion++;

            // 如果使用VXE表格，清空其选择状态
            if (this.$refs.recordTable) {
                this.$refs.recordTable.clearCheckboxRow();
            }

            this.saveSearchForm();
            this.fetchRecordData();
        },
        handleReset() {
            this.searchForm = {
                dateRange: [],
                searchField: '',
                searchValue: '',
                searchType: 'contains',
                replaceField: '',
                replaceSearchValue: '',
                replaceValue: '',
                incomeExpenseAttribute: '',
                replaceWholeField: false
            }
            this.handleSearch()
        },
        async handleReplace() {
            if (!this.searchForm.replaceField || !this.searchForm.replaceSearchValue) {
                this.$message.warning('请选择替换字段并输入要替换的内容')
                return
            }

            try {
                const field = this.searchForm.replaceField
                const searchValue = this.searchForm.replaceSearchValue
                const replaceValue = this.searchForm.replaceValue
                const selectedIds = this.hasSelected ? this.selectedRowKeys.join(',') : ''
                const replaceWholeField = this.searchForm.replaceWholeField
                const searchType = this.searchForm.searchType

                // 获取当前的搜索参数
                const searchParams = this.getSearchParams()

                // 调用替换接口
                await request.post(API.REPLACE_RECORD_FIELD, {
                    platform: this.platform,
                    user_id: this.userId,
                    replace: {
                        field: field,
                        search_value: searchValue,
                        replace_value: replaceValue,
                        whole_field: replaceWholeField
                    },
                    filter: {
                        selected_ids: selectedIds,
                        begin_time: searchParams.begin_time,
                        end_time: searchParams.end_time,
                        search: searchParams.search,
                        search_type: searchType
                    }
                })

                this.$message.success('替换成功')
                // 刷新数据
                this.fetchRecordData()
            } catch (error) {
                this.$message.error(error.message || '替换失败')
            }
        },
        async handleExport() {
            const messageKey = 'exportLoading';
            this.$message.loading({ content: '正在导出数据...', key: messageKey, duration: 0 });
            try {
                // 直接使用当前表格中的数据
                let exportData = [];

                if (this.selectedRowKeys.length > 0) {
                    // 如果有选中的行，只导出选中的数据
                    const selectedData = this.recordData.filter(item => this.selectedRowKeys.includes(item.key));
                    if (selectedData.length > 0) {
                        exportData = selectedData;
                        this.$message.info(`将导出选中的 ${selectedData.length} 条记录`);
                    }
                } else {
                    // 否则导出当前页面的所有数据
                    exportData = this.recordData;
                }

                if (exportData.length === 0) {
                    this.$message.error({ content: '没有可导出的数据', key: messageKey });
                    return;
                }

                console.log('准备导出数据:', exportData.length, '条记录');



                // 转换数据格式为适合Excel的格式
                const excelData = exportData.map(item => {
                    const exportItem = {};

                    // 添加ID字段用于后续导入时判断是更新还是新增
                    exportItem['ID'] = String(item.id || '');

                    // 根据当前显示的列动态生成导出数据
                    this.columns.forEach(col => {
                        if (col.dataIndex && col.dataIndex !== 'action') {
                            let value = item[col.dataIndex];

                            // 所有数据都转换为字符串格式
                            if (col.type === 'date' && value) {
                                // 日期转为字符串
                                exportItem[col.title] = "'" + String(value || '');
                            } else if (col.type === 'boolean' && value !== undefined) {
                                exportItem[col.title] = value ? '是' : '否';
                            } else if ((col.dataIndex === 'transaction_amount' || col.dataIndex === 'balance') && value !== undefined) {
                                // 金额类型转为字符串
                                exportItem[col.title] = String(value || '');
                            } else if (col.type === 'number' && value !== undefined) {
                                // 数字类型转为字符串
                                exportItem[col.title] = String(value || '');
                            } else {
                                exportItem[col.title] = value !== undefined && value !== null ? String(value) : '';
                            }
                        }
                    });

                    return exportItem;
                });

                console.log('转换后的导出数据:', excelData.length, '条记录');

                if (excelData.length === 0) {
                    this.$message.error({ content: '导出数据转换失败', key: messageKey });
                    return;
                }

                // 创建工作表
                const worksheet = XLSX.utils.json_to_sheet(excelData);

                // 设置列宽
                const wscols = Object.keys(excelData[0] || {}).map(() => ({ wch: 15 }));
                worksheet['!cols'] = wscols;

                // 设置单元格格式为文本
                if (excelData.length > 0) {
                    const range = XLSX.utils.decode_range(worksheet['!ref']);
                    for (let R = range.s.r; R <= range.e.r; ++R) {
                        for (let C = range.s.c; C <= range.e.c; ++C) {
                            const cell_address = { c: C, r: R };
                            const cell_ref = XLSX.utils.encode_cell(cell_address);
                            if (!worksheet[cell_ref]) continue;
                            worksheet[cell_ref].t = 's'; // 设置为字符串类型
                        }
                    }
                }

                const workbook = XLSX.utils.book_new();
                XLSX.utils.book_append_sheet(workbook, worksheet, '交易记录');

                // 导出文件
                const platformName = this.getPlatformName();
                const fileName = `${platformName}交易记录_${moment().format('YYYY-MM-DD')}.xlsx`;
                XLSX.writeFile(workbook, fileName);

                this.$message.success({ content: '导出成功', key: messageKey });
            } catch (error) {
                console.error('导出失败:', error);
                this.$message.error({ content: '导出失败：' + error.message, key: messageKey });
            }
        },
        handleBatchCopy() {
            // 获取选中的行
            const selectedRows = this.$refs.recordTable.getCheckboxRecords()
            // 复制
            if (selectedRows.length > 0) {
                // 复制
                this.handleCopy(selectedRows.map(row => row.id))
            }
        },
        beforeUploadExcel(file) {
            // 验证文件类型
            const isExcel = file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
                file.type === 'application/vnd.ms-excel';
            if (!isExcel) {
                this.$message.error('只能上传Excel文件!');
                return false;
            }

            // 验证文件大小
            const isLt100M = file.size / 1024 / 1024 < 100;
            if (!isLt100M) {
                this.$message.error('文件大小不能超过100MB!');
                return false;
            }

            // 将文件添加到列表中
            this.excelFileList = [file];

            // 阻止自动上传
            return false;
        },
        async handleImportExcel() {
            if (this.excelFileList.length === 0) {
                this.$message.warning('请先选择Excel文件!');
                return;
            }

            try {
                this.importLoading = true;
                const file = this.excelFileList[0];

                // 如果选择了覆盖全部数据，先清空用户记录
                if (this.importOptions.overwriteData) {
                    try {
                        // 显示确认对话框
                        const confirmed = await new Promise(resolve => {
                            this.$confirm({
                                title: '确认操作',
                                content: `确定要清空${this.getPlatformName()}的所有记录并导入新数据吗？此操作不可恢复！`,
                                okText: '确认',
                                okType: 'danger',
                                cancelText: '取消',
                                onOk: () => resolve(true),
                                onCancel: () => resolve(false)
                            });
                        });

                        if (!confirmed) {
                            this.importLoading = false;
                            return;
                        }

                        // TODO: 调用清空用户记录接口
                        // 由于接口暂时没有，这里先模拟一个接口调用
                        // this.$message.loading('正在清空历史数据...', 0);
                        await request.post(API.DELETE_ALL_RECORDS, {
                            platform: this.platform,
                            user_id: this.userId
                        });
                        // this.$message.success('历史数据已清空');
                    } catch (error) {
                        this.$message.error('清空数据失败: ' + error.message);
                        this.importLoading = false;
                        return;
                    }
                }

                // 读取Excel文件
                const reader = new FileReader();
                reader.onload = async (e) => {
                    try {
                        const data = new Uint8Array(e.target.result);
                        const workbook = XLSX.read(data, { type: 'array' });

                        // 获取第一个工作表
                        const firstSheetName = workbook.SheetNames[0];
                        const worksheet = workbook.Sheets[firstSheetName];

                        // 将工作表转换为JSON
                        const jsonData = XLSX.utils.sheet_to_json(worksheet);

                        if (jsonData.length === 0) {
                            this.$message.error('Excel文件中没有数据!');
                            this.importLoading = false;
                            return;
                        }

                        // 处理导入的数据
                        const importData = []; // 新增记录
                        const updateData = []; // 更新记录
                        const columnTitleMap = {};

                        // 创建列标题到字段名的映射
                        this.columns.forEach(col => {
                            if (col.dataIndex && col.dataIndex !== 'action') {
                                columnTitleMap[col.title] = {
                                    field: col.dataIndex,
                                    type: col.type
                                };
                            }
                        });

                        // 转换Excel数据为系统数据格式
                        jsonData.forEach(row => {
                            const newRecord = {};
                            let recordId = null;

                            // 获取ID字段值
                            if ('ID' in row) {
                                recordId = row['ID'];
                            }

                            // 遍历Excel中的每一列
                            Object.keys(row).forEach(excelColumn => {

                                if (excelColumn === 'ID') return; // 跳过ID字段，不作为数据字段处理
                                // 查找对应的字段信息
                                const fieldInfo = columnTitleMap[excelColumn];
                                if (fieldInfo) {
                                    const { field, type } = fieldInfo;
                                    let value = row[excelColumn];

                                    // 根据字段类型处理数据
                                    if (type === 'date' && value) {
                                        // 处理不同类型的日期输入
                                        if (typeof value === 'number') {
                                            if (value < 100000) { // Excel日期序列号
                                                // Excel日期序列号转换为JS日期
                                                // Excel以1900-01-01为基准日，且错误地将1900当作闰年
                                                const excelEpoch = new Date(1899, 11, 30);
                                                // 计算天数和时间
                                                const days = Math.floor(value);
                                                const fraction = value - days;
                                                const millisecondsInDay = 24 * 60 * 60 * 1000;
                                                const date = new Date(Math.floor(excelEpoch.getTime() / 1000 + (days * millisecondsInDay) / 1000 + (fraction * millisecondsInDay) / 1000) * 1000);

                                                // 转换为Unix时间戳
                                                newRecord[field] = moment(date).unix();
                                                console.log(`[Excel日期转换] ${field}: ${value} -> ${moment(date).format('YYYY-MM-DD HH:mm:ss')} (${newRecord[field]})`);
                                            } else if (value > 10000000000) { // 毫秒时间戳
                                                newRecord[field] = Math.floor(value / 1000);
                                                console.log(`[毫秒时间戳] ${field}: ${value} -> ${moment(value).format('YYYY-MM-DD HH:mm:ss')} (${newRecord[field]})`);
                                            } else { // 秒时间戳
                                                newRecord[field] = value;
                                                console.log(`[秒时间戳] ${field}: ${value} -> ${moment(value * 1000).format('YYYY-MM-DD HH:mm:ss')} (${newRecord[field]})`);
                                            }
                                        } else if (typeof value === 'string') {
                                            try {
                                                // value去掉'
                                                value = value.replace(/'/g, '');
                                                // 尝试解析日期字符串
                                                const date = moment(value);
                                                if (date.isValid()) {
                                                    newRecord[field] = date.unix();
                                                    console.log(`[日期字符串] ${field}: ${value} -> ${date.format('YYYY-MM-DD HH:mm:ss')} (${newRecord[field]})`);
                                                } else {
                                                    console.warn(`无效的日期字符串: ${value}`);
                                                    newRecord[field] = null;
                                                }
                                            } catch (e) {
                                                console.warn(`日期转换失败: ${value}`, e);
                                                newRecord[field] = null;
                                            }
                                        } else {
                                            console.warn(`未知的日期类型: ${typeof value}, 值: ${value}`);
                                            newRecord[field] = null;
                                        }
                                    } else if (type === 'boolean') {
                                        newRecord[field] = value === '是' || value === true;
                                    } else if (type === 'number') {
                                        newRecord[field] = Number(value);
                                    } else {
                                        newRecord[field] = value;
                                    }
                                }
                            });

                            // 添加必要的字段
                            newRecord.platform = this.platform;
                            newRecord.user_id = this.userId;

                            // 如果已经清空了数据，则所有记录都作为新增处理
                            if (this.importOptions.overwriteData) {
                                importData.push(newRecord);
                            } else {
                                // 根据是否有ID判断是更新还是新增
                                if (recordId && !isNaN(recordId)) {
                                    newRecord.id = recordId;
                                    updateData.push(newRecord);
                                } else {
                                    importData.push(newRecord);
                                }
                            }
                        });

                        try {
                            let successCount = 0;
                            let addCount = 0;
                            let updateCount = 0;

                            // 处理新增记录
                            if (importData.length > 0) {
                                const addRes = await request.post(API.ADD_RECORD, {
                                    platform: this.platform,
                                    user_id: this.userId,
                                    records: importData
                                });

                                if (addRes.code === 1) {
                                    successCount += importData.length;
                                    addCount = importData.length;
                                } else {
                                    throw new Error(addRes.msg || '批量添加记录失败');
                                }
                            }


                            if (updateData.length > 0) {
                                // 处理更新记录
                                try {
                                    const updateRes = await request.post(API.BATCH_EDIT_RECORD, {
                                        platform: this.platform,
                                        data: updateData
                                    });

                                    if (updateRes.code === 1) {
                                        successCount += updateData.length;
                                        updateCount = updateData.length;
                                    }
                                } catch (error) {
                                    console.warn(`更新记录失败:`, error);
                                }
                            }

                            this.$message.success(`成功处理 ${successCount} 条记录（新增: ${addCount}, 更新: ${updateCount}）`);
                            this.showImportExcelModal = false;
                            this.excelFileList = [];
                            // 重置覆盖选项
                            this.importOptions.overwriteData = false;

                            // 刷新数据
                            this.fetchRecordData();
                        } catch (error) {
                            this.$message.error('导入处理失败: ' + error.message);
                        }
                    } catch (error) {
                        this.$message.error('解析Excel文件失败: ' + error.message);
                    } finally {
                        this.importLoading = false;
                    }
                };

                reader.onerror = () => {
                    this.$message.error('读取文件失败!');
                    this.importLoading = false;
                };

                reader.readAsArrayBuffer(file);
            } catch (error) {
                this.$message.error('导入Excel失败: ' + error.message);
                this.importLoading = false;
            }
        },
        getPlatformName() {
            const platformMap = {
                'js': '建设银行',
                'zg': '中国银行',
                'yz': '邮政银行',
                'ny': '农业银行',
                'wx': '微信',
                'gs': '工商银行',
                'pa': '平安银行',
                'nyzz': '农业纸质',
                'xy': '兴业银行',
                'jt': '交通银行'
            };
            return platformMap[this.platform] || '银行';
        },
        handleTableChange(pagination, filters, sorter) {
            // 只处理排序
            let sort = 'id desc'
            if (sorter && sorter.field === 'transaction_date') {
                sort = `transaction_date ${sorter.order === 'ascend' ? 'asc' : 'desc'}`
            }
            this.fetchRecordData(sort)
        },
        getSearchParams(sort = 'transaction_date ') {
            const params = {
                platform: this.platform,
                user_id: this.userId,
                page: 1, // 固定为第1页
                limit: 1000, // 使用一个较大的值获取所有数据
                sort: sort,
                search: {},
                desensitize: 0, // 展示场景不脱敏(需编辑);打印场景传 1
            }

            // 如果选择了时间范围，则添加时间参数
            const [startDate, endDate] = this.searchForm.dateRange || []
            if (startDate && endDate) {
                params.begin_time = Math.floor(startDate.valueOf() / 1000)
                params.end_time = Math.floor(endDate.valueOf() / 1000)
            } else {
                // 时间范围为空时，确保不带有begin_time和end_time参数
                delete params.begin_time
                delete params.end_time
            }


            // 添加搜索字段和内容到 search 对象中
            if (this.searchForm.searchField && this.searchForm.searchValue) {
                params.search[this.searchForm.searchField] = this.searchForm.searchValue
                params.search_type = this.searchForm.searchType
            }

            // 添加收支属性搜索条件
            if (this.searchForm.incomeExpenseAttribute) {
                params.search['income_expense_attribute'] = this.searchForm.incomeExpenseAttribute
            }

            return params
        },
        async fetchRecordData(sort) {
            this.loading = true
            try {
                const params = this.getSearchParams(sort)
                console.log('搜索参数:', params) // 添加日志查看搜索参数
                const res = await request.post(API.TRANSFER_RECORD, params)
                const { data, total_income, total_expense } = res.data

                // 如果返回的数据为空，可能是时间范围问题，添加提示
                if (data.length === 0 && this.searchForm.dateRange && this.searchForm.dateRange.length === 2) {
                    this.$message.info('当前时间范围内无数据，您可以清除时间范围尝试搜索全部数据')
                }

                this.recordData = data.map(item => ({
                    ...item
                }))

                this.recordTableOptions.data = data

                // 更新总条数（虽然不分页，但提供总条数信息）
                this.pagination.total = data.length
                this.totalIncome = total_income
                this.totalExpense = total_expense

                // 更新找到负数记录
                this.negativeBalanceRecord = this.recordTableOptions.data.find(item => Number(item.balance) <= 0)

            } catch (error) {
                this.$message.error(error.message || '获取数据失败')
            } finally {
                this.loading = false
            }
        },
        onSelectionChange(selectedRowKeys, selectedRows) {
            this.selectedRowKeys = selectedRowKeys;
            this.selectedRows = selectedRows;
        },
        handleAdd() {
            this.modalTitle = '添加记录'
            this.currentRecord = {
                transaction_date: moment(),
                posting_date: moment(),
                transaction_amount: 0,
                balance: 0,
                merchant_code: '',
                summary: '消费',
                counterparty_account: '',
                counterparty_name: '',
                income_expense_attribute: '支出'
            }
            this.currentRecordIndex = -1
            this.modalVisible = true
        },
        handleEdit(record) {
            this.modalTitle = '编辑记录'
            this.currentRecord = {
                ...record,
                transaction_date: moment(record.transaction_date * 1000),
                posting_date: moment(record.posting_date * 1000)
            }
            this.currentRecordIndex = this.recordData.findIndex(item => item.id === record.id)
            this.modalVisible = true
        },
        handleModalCancel() {
            this.modalVisible = false
        },
        async handleDelete(record) {
            try {
                await request.post(API.DELETE_RECORDS, {
                    ids: record.id.toString(),
                    platform: this.platform,
                    user_id: this.userId
                })
                this.$message.success('删除成功')
                this.fetchRecordData()
            } catch (error) {
                this.$message.error(error.message || '删除失败')
            }
        },
        async handleBatchDelete() {
            try {

                await request.post(API.DELETE_RECORDS, {
                    ids: this.selectedRowKeys.join(','),
                    platform: this.platform,
                    user_id: this.userId
                })
                this.$message.success('批量删除成功')
                this.selectedRowKeys = []
                this.fetchRecordData()
            } catch (error) {
                this.$message.error(error.message || '批量删除失败')
            }
        },
        handleGotoBatchConfig() {
            this.$router.push({
                name: 'BatchGenerateConfig',
                params: {
                    platform: this.platform,
                    userId: this.userId
                }
            })
        },
        toggleEdit(record) {
            // 如果已经有其他行在编辑，先保存那一行
            const editingRecord = this.recordData.find(r => r.editable && r.id !== record.id)
            if (editingRecord) {
                this.saveRow(editingRecord)
            }

            // 切换当前行的编辑状态
            record.editable = !record.editable
            if (record.editable) {
                this.cacheData = [...this.recordData]
                // 转换日期格式
                record.transaction_date_str = this.formatTime(record.transaction_date)
                record.posting_date_str = this.formatTime(record.posting_date)
            }
        },
        async saveRow(record) {
            try {
                if (record.transaction_date_str) {
                    record.transaction_date = Math.floor(moment(record.transaction_date_str, 'YYYY-MM-DD HH:mm:ss').valueOf() / 1000)
                    delete record.transaction_date_str
                }
                if (record.posting_date_str) {
                    record.posting_date = Math.floor(moment(record.posting_date_str, 'YYYY-MM-DD HH:mm:ss').valueOf() / 1000)
                    delete record.posting_date_str
                }

                var data = {}
                for (const key in record) {
                    if (record[key] !== undefined) {
                        data[key] = record[key]
                    }
                }

                // 准备更新的数据
                const params = {
                    platform: this.platform,
                    id: record.id,
                    data: data
                }

                // 调用更新接口
                await request.post(API.EDIT_RECORD, params)
                // record.editable = false
                // this.$message.success('保存成功')

                // 重新获取数据以确保显示最新状态
                // this.fetchRecordData()
            } catch (error) {
                this.$message.error(error.message || '保存失败')
            }
        },
        cancelEdit(record) {
            const index = this.recordData.findIndex(item => item.id === record.id)
            Object.assign(this.recordData[index], this.cacheData[index])
            record.editable = false
        },
        handleOutsideClick() {
            const editingRecord = this.recordData.find(record => record.editable)
            if (editingRecord) {
                this.saveRow(editingRecord)
            }
        },
        getRowClassName(record) {
            const classes = [];
            if (record.interest_day === 1) {
                classes.push('interest-day-row');
            }
            if (Number(record.balance) < 0) {
                classes.push('negative-balance-row');
            }
            return classes.join(' ');
        },
        customRow(record) {
            return {
                on: {
                    click: (event) => {
                        // 如果点击的是可编辑单元格或操作按钮，不做任何处理
                        if (event.target.closest('.editable-cell') ||
                            event.target.closest('.editable-row-operations')) {
                            return
                        }
                    }
                }
            }
        },
        formatAmount(amount) {
            return Number(amount).toLocaleString('zh-CN', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            })
        },
        // 格式化负余额文本，确保单行显示
        formatNegativeBalanceText(record) {
            if (!record) return '';

            const date = record.transaction_date || '';
            const balance = this.formatAmount(record.balance);
            const summary = record.summary || '';

            // 限制摘要长度，超过8个字符使用省略号
            const truncatedSummary = summary.length > 8 ? summary.substring(0, 8) + '...' : summary;

            return `${date} ${truncatedSummary} ${balance}`;
        },
        handleCopy(recordIds) {
            console.log('复制', recordIds)

            // 添加新记录
            request.post(API.COPY_RECORDS, {
                platform: this.platform,
                user_id: this.userId,
                ids: recordIds
            }).then(res => {
                if (res.code === 1) {
                    this.$message.success('复制成功');
                    this.fetchRecordData();
                } else {
                    this.$message.error(res.msg || '复制失败');
                }
            }).catch(error => {
                this.$message.error(error.message || '复制失败');
            });
        },
        // 保存搜索表单到本地存储
        saveSearchForm() {
            const storageKey = `transfer_record_search_${this.platform}_${this.userId}`;
            const searchFormData = {
                ...this.searchForm,
                dateRange: this.searchForm.dateRange && this.searchForm.dateRange.length === 2 ? [
                    this.searchForm.dateRange[0].format('YYYY-MM-DD'),
                    this.searchForm.dateRange[1].format('YYYY-MM-DD')
                ] : null
            };
            localStorage.setItem(storageKey, JSON.stringify(searchFormData));
        },
        // 从本地存储恢复搜索表单
        restoreSearchForm() {
            try {
                const storageKey = `transfer_record_search_${this.platform}_${this.userId}`;
                const savedData = localStorage.getItem(storageKey);
                if (savedData) {
                    const searchFormData = JSON.parse(savedData);
                    // 恢复日期范围
                    if (searchFormData.dateRange) {
                        searchFormData.dateRange = [
                            moment(searchFormData.dateRange[0]),
                            moment(searchFormData.dateRange[1])
                        ];
                    } else {
                        searchFormData.dateRange = [];
                    }
                    this.searchForm = searchFormData;
                }
            } catch (error) {
                console.warn('恢复搜索条件失败:', error);
                // 如果恢复失败，使用默认值
                this.searchForm = {
                    dateRange: [],
                    searchField: '',
                    searchValue: '',
                    searchType: 'contains',
                    replaceField: '',
                    replaceSearchValue: '',
                    replaceValue: '',
                    incomeExpenseAttribute: '',
                    replaceWholeField: false
                };
            }
        },
        scrollToRow(row) {
            console.log('滚动到行', row);
            // 使用VXE表格的API滚动到指定行
            if (this.$refs.recordTable) {
                console.log('表格实例', this.$refs.recordTable);
                // 先清除所有选中状态
                // this.$refs.recordTable.clearCheckboxRow();
                this.$refs.recordTable.scrollToRow(row);

                // 选中
                this.$refs.recordTable.setCurrentRow(row);
            }
        },
        onSelectAllChange(e) {
            const checked = e.target.checked
            const newSelectedKeys = checked ? this.recordData.map(item => item.key) : []
            this.onSelectionChange(newSelectedKeys, checked ? this.recordData : [])
        },
        onSelectChange(record, checked) {
            const selectedKeys = [...this.selectedRowKeys]
            if (checked) {
                selectedKeys.push(record.key)
            } else {
                const index = selectedKeys.indexOf(record.key)
                if (index !== -1) {
                    selectedKeys.splice(index, 1)
                }
            }
            this.onSelectionChange(selectedKeys, this.recordData.filter(item => selectedKeys.includes(item.key)))
        },
        onRowClick(record, event) {
            // 如果是编辑状态，或者点击的是可编辑单元格或操作按钮，不处理选择
            if (record.editable ||
                event?.target?.closest('.editable-cell') ||
                event?.target?.closest('.editable-row-operations')) {
                return
            }
            this.onSelectChange(record, !this.selectedRowKeys.includes(record.key))
        },
        // 处理表格选择变更
        handleCheckboxChange({ checked, row, records, $event }) {
            // 实时检测Shift键状态（优先使用事件对象的shiftKey属性）
            const isShiftPressed = this.detectShiftKey($event);
            console.log('checkbox change', checked, row, records, 'shift:', isShiftPressed)

            // 直接使用 records 作为所有选中行 - 这是 vxe-table 提供的所有当前选中行
            if (records && Array.isArray(records)) {
                // 完全替换当前选中行
                this.selectedRows = [...records]
                // 记录选中的 key
                this.selectedRowKeys = records.map(item => item.id || item._X_ROW_KEY)
                // 强制版本更新
                this.selectedRowsVersion++
            }

            if (isShiftPressed) {
                // 找到当前行在表格数据中的索引位置
                const rowIndex = this.$refs.recordTable.data.findIndex(item => item.id === row.id)
                console.log('shift range end', row.id, 'index:', rowIndex)
                this.selectedIndexes[1] = rowIndex
                this.handleSelectedIds()
            } else {
                // 找到当前行在表格数据中的索引位置
                const rowIndex = this.$refs.recordTable.data.findIndex(item => item.id === row.id)
                console.log('shift range start', row.id, 'index:', rowIndex)
                this.selectedIndexes[0] = rowIndex
                // 重置范围结束索引
                this.selectedIndexes[1] = 0
            }

            // 强制更新视图
            this.$forceUpdate()
        },
        // 处理数据变更
        async handleDataChange({ row, field, value }) {
            try {
                console.log('修改数据:', { row, field, value });
                // 准备更新的数据
                const params = {
                    platform: this.platform,
                    id: row.id,
                    data: {
                        ...row
                    }
                };

                console.log('准备更新的数据:', params);

                // 调用更新接口
                const res = await request.post(API.EDIT_RECORD, params);
                if (res.code === 1) {
                    // this.$message.success('保存成功');
                    // 刷新数据
                    // this.fetchRecordData();
                } else {
                    throw new Error(res.msg || '保存失败');
                }
            } catch (error) {
                this.$message.error(error.message || '保存失败');
            }
        },
        // 绑定键盘事件监听器
        bindKeyboardEvents() {
            window.addEventListener('keydown', this.handleKeydown, true);
            window.addEventListener('keyup', this.handleKeyup, true);
        },
        // 解绑键盘事件监听器
        unbindKeyboardEvents() {
            window.removeEventListener('keydown', this.handleKeydown, true);
            window.removeEventListener('keyup', this.handleKeyup, true);
        },
        // 重置Shift状态
        resetShiftState() {
            this.isShift = false;
            this.selectedIndexes = [0, 0];
            console.log('Shift状态已重置');
        },
        // 检测Shift键状态（多重检测机制）
        detectShiftKey(event) {
            // 优先使用事件对象的shiftKey属性
            if (event && typeof event.shiftKey === 'boolean') {
                return event.shiftKey;
            }
            // 回退到全局状态
            return this.isShift;
        },
        // 处理窗口失去焦点
        handleWindowBlur() {
            console.log('窗口失去焦点，重置Shift状态');
            this.resetShiftState();
        },
        // 处理窗口获得焦点
        handleWindowFocus() {
            console.log('窗口获得焦点');
            this.resetShiftState();
        },
        // 处理页面可见性变化
        handleVisibilityChange() {
            if (document.hidden) {
                console.log('页面隐藏，重置Shift状态');
                this.resetShiftState();
            }
        },
        handleKeydown(e) {
            if (e.key === 'Shift') {
                this.isShift = true
                console.log('Shift键已按下', 'timestamp:', Date.now())
            }
        },
        // 监听按键松开
        handleKeyup(e) {
            if (e.key === 'Shift') {
                this.isShift = false
                console.log('Shift键已松开', 'timestamp:', Date.now())
                // 延迟重置选择索引，避免快速按键导致的状态混乱
                setTimeout(() => {
                    this.selectedIndexes[1] = 0;
                }, 50);
            }
        },
        // 进行选中计算
        handleSelectedIds() {
            // 如果两个索引都有效，则进行选中计算
            if (this.selectedIndexes[0] >= 0 && this.selectedIndexes[1] > 0) {
                console.log('执行范围选择', this.selectedIndexes[0], 'to', this.selectedIndexes[1]);

                // 确保索引顺序正确
                let startIndex = this.selectedIndexes[0];
                let endIndex = this.selectedIndexes[1];
                if (startIndex > endIndex) {
                    [startIndex, endIndex] = [endIndex, startIndex];
                }

                // 验证索引范围有效性
                const dataLength = this.$refs.recordTable.data.length;
                if (startIndex >= 0 && endIndex < dataLength) {
                    // 基于索引范围选择行（包含起始和结束索引）
                    let selectedRows = this.$refs.recordTable.data.slice(startIndex, endIndex + 1);
                    console.log('范围选择的行数:', selectedRows.length);

                    // 加上已选中的并去重
                    selectedRows.push(...this.selectedRows);
                    selectedRows = [...new Set(selectedRows)];

                    // setCheckboxRow
                    this.$refs.recordTable.setCheckboxRow(selectedRows, true);
                    // 记录选中的 key
                    this.selectedRowKeys = selectedRows.map(item => item.id || item._X_ROW_KEY);
                    // 记录选中的行
                    this.selectedRows = selectedRows;
                    // 强制版本更新
                    this.selectedRowsVersion++;
                }

                // 清空selectedIndexes
                this.selectedIndexes = [0, 0];
            }
        },
        // 处理全选
        handleCheckboxAll({ checked, $event }) {
            if (checked) {
                // 使用索引而不是ID
                this.selectedIndexes[0] = 0
                this.selectedIndexes[1] = this.$refs.recordTable.data.length - 1
            }
            this.handleSelectedIds()
        }
    }

}
</script>

<style scoped>
.js-transfer-record-table {
    width: 100%;
    display: flex;
    flex-direction: column;
    height: 100%;
    /* 添加高度100% */
}

.search-form {
    display: flex;
    flex-direction: column;
}

.table-toolbar {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.editable-cell {
    position: relative;
    padding: 5px 12px;
    cursor: pointer;
}

.editable-row-operations a {
    margin-right: 8px;
}

/* 添加虚拟列表相关样式 */
.virtual-table {
    flex: 1;
    overflow: hidden !important;
}

.virtual-table :deep(.ant-table) {
    height: 100%;
}

.virtual-table :deep(.ant-table-body) {
    height: 100% !important;
    max-height: none !important;
}

.virtual-table :deep(.ant-table-body > table) {
    padding: 0;
    margin: 0;
}

/* 优化滚动条样式 */
.virtual-table :deep(.ant-table-body::-webkit-scrollbar) {
    width: 6px;
    height: 6px;
}

.virtual-table :deep(.ant-table-body::-webkit-scrollbar-thumb) {
    background: #ccc;
    border-radius: 3px;
}

.virtual-table :deep(.ant-table-body::-webkit-scrollbar-track) {
    background: #f1f1f1;
}

/* 添加紧凑型表格样式 */
.compact-table {
    :deep(.ant-table-tbody > tr > td) {
        padding: 4px 8px;
    }

    :deep(.ant-table-thead > tr > th) {
        padding: 8px 8px;
    }
}

/* 修改利息日行的样式，使用更深的绿色 */
:deep(.interest-day-row) {
    background-color: #b7eb8f;
    /* 更深的绿色背景 */
}

/* 鼠标悬停时的背景色也相应调深 */
:deep(.interest-day-row:hover > td) {
    background-color: #95de64 !important;
    /* 悬停时的深绿色 */
}

/* 添加行点击样式 */
:deep(.ant-table-tbody > tr) {
    cursor: pointer;
}

/* 优化信息行布局 */
.info-row {
    align-items: center; /* 垂直居中对齐 */
    display: flex;
    flex-wrap: wrap;
}

.statistics-info {
    padding: 8px;
    border-radius: 4px;
    display: flex;
    align-items: center;
    gap: 16px;
    flex-wrap: wrap;
}

.statistics-info.compact {
    padding: 4px 0; /* 去掉左右内边距，保持垂直内边距 */
    gap: 12px;
    /* 移除背景色和边框，与页面背景保持一致 */
}

.statistics-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    white-space: nowrap;
}

.statistics-item .label {
    color: rgba(0, 0, 0, 0.85);
    margin-right: 6px;
    font-size: 13px;
    font-weight: 500; /* 增加字体重量以提升可读性 */
}

.income {
    font-weight: bold;
    color: #f5222d;
    font-size: 13px;
    padding: 2px 6px; /* 添加轻微的内边距 */
    background-color: rgba(245, 34, 45, 0.1); /* 添加淡色背景以突出显示 */
    border-radius: 3px;
}

.expense {
    font-weight: bold;
    color: #52c41a;
    font-size: 13px;
    padding: 2px 6px; /* 添加轻微的内边距 */
    background-color: rgba(82, 196, 26, 0.1); /* 添加淡色背景以突出显示 */
    border-radius: 3px;
}

/* 添加表格容器样式 */
.table-container {
    min-height: 500px;
    max-height: calc(100vh - 300px);
    display: flex;
    flex-direction: column;
    overflow: hidden;
    border: 1px solid #f0f0f0;
    border-radius: 2px;
    position: relative;
    /* 添加相对定位 */
}

/* 表格底部信息 */
.table-footer {
    padding: 8px;
    border-top: 1px solid #f0f0f0;
    display: flex;
    justify-content: flex-end;
    align-items: center;
}

.total-info {
    color: rgba(0, 0, 0, 0.85);
}

.negative-balance-warning {
    margin-top: 16px;
}

.negative-balance-warning.compact {
    margin-top: 0;
    padding: 0;
}

/* 优化的负余额警告样式 */
.negative-balance-content {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 4px 0; /* 去掉背景色后调整内边距 */
    /* 移除背景色和边框，与页面背景保持一致 */
    font-size: 12px;
    white-space: nowrap;
    overflow: hidden;
    justify-content: flex-end; /* 右对齐，与统计信息形成对比 */
}

.warning-label {
    color: #d46b08;
    font-weight: 500;
    flex-shrink: 0;
    font-size: 13px; /* 稍微增大字体以保持可读性 */
}

.negative-balance-tag {
    cursor: pointer;
    margin: 0 !important;
    overflow: hidden;
    flex-shrink: 1;
    background-color: #fff7e6 !important; /* 保持标签的背景色以突出显示 */
    border-color: #ffd591 !important;
    display: flex;
    justify-content: center;
    align-items: center;
}

.negative-balance-tag:hover {
    border-color: #ff7a00 !important;
    transform: translateY(-1px); /* 添加轻微的悬停效果 */
    box-shadow: 0 2px 4px rgba(255, 122, 0, 0.2);
}

.tag-content {
    display: inline-block;
    width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 11px;
}

/* 响应式设计 */
@media (max-width: 768px) {
    .info-row {
        flex-direction: column; /* 小屏幕时垂直排列 */
        align-items: stretch;
    }

    .info-row .ant-col {
        margin-bottom: 8px;
        width: 100% !important; /* 小屏幕时占满宽度 */
    }

    .statistics-info.compact {
        gap: 8px;
        justify-content: space-between;
        padding: 8px 0; /* 增加垂直内边距 */
    }

    .statistics-item .label {
        font-size: 12px;
    }

    .income, .expense {
        font-size: 12px;
        padding: 1px 4px; /* 调整小屏幕的内边距 */
    }

    .negative-balance-warning.compact {
        margin-top: 8px;
    }

    .negative-balance-content {
        justify-content: flex-start; /* 小屏幕时左对齐 */
        gap: 4px;
        padding: 8px 0; /* 增加垂直内边距 */
    }

    .warning-label {
        font-size: 12px; /* 保持与统计信息一致的字体大小 */
    }

    .negative-balance-tag {
        max-width: 100%;
        width: auto; /* 自适应宽度 */
    }

    .tag-content {
        font-size: 11px; /* 稍微增大字体 */
    }
}

.negative-rows-list {
    margin-top: 8px;
    max-height: 100px;
    overflow-y: auto;
}

:deep(.negative-balance-row) {
    background-color: #fff1f0;
}

:deep(.negative-balance-row:hover > td) {
    background-color: #ffccc7 !important;
}

:deep(.highlight-row) {
    animation: highlight-animation 2s;
}

@keyframes highlight-animation {
    0% {
        background-color: #ffd591;
    }

    100% {
        background-color: transparent;
    }
}

.table-header {
    background: #fafafa;
    border-bottom: 1px solid #f0f0f0;
}

.custom-table {
    border-collapse: collapse;
}

.custom-table th {
    padding: 8px;
    text-align: left;
    font-weight: 500;
    background: #fafafa;
    border-bottom: 1px solid #f0f0f0;
}

.scroller {
    flex: 1;
    overflow-x: auto;
}

.scroller tr {
    display: flex;
    border-bottom: 1px solid #f0f0f0;
}

.scroller td {
    padding: 4px 8px;
    display: flex;
    align-items: center;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.selection-column {
    width: 60px;
    flex-shrink: 0;
}

.operation-column {
    width: 150px;
    flex-shrink: 0;
}

.red {
    color: red !important;
}

.green {
    color: green !important;
}

/* 搜索表单样式 */
.search-form {
    background: #fff;
    border: 1px solid #f0f0f0;
    border-radius: 6px;
}

.search-form-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-bottom: 1px solid #f0f0f0;
}

.search-form-title {
    margin: 0;
    font-size: 16px;
    font-weight: 500;
    color: #262626;
}

.expand-toggle-btn {
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 4px 8px;
    color: #1890ff;
    transition: all 0.3s ease;
}

.expand-toggle-btn:hover {
    color: #40a9ff;
    background-color: #f6ffed;
}

.search-form-content {
    transition: all 0.3s ease;
}

/* 过渡动画 */
.a-collapse-transition {
    transition: all 0.3s ease;
}
</style>